import { Component, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { FilterSettings } from './project-filters/filter-settings';
import { cleanDescription, formatDate, shippedColor } from '../master-schedule/shared';
import { ProjectCardComponent, ProjectCardView } from './project-card/project-card.component';
import { EngineeringToolsApiService } from 'src/app/shared/services/engineering-tools-api';
import { MultiSelectOption } from 'src/app/shared/components/multi-select-dropdown/multi-select-dropdown';
import { toolTypeRouteMap } from '../tools/route-type-map';

@Component({
  selector: 'projects',
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss',
  standalone: false
})
export class ProjectsComponent implements OnDestroy {
  
  constructor(
    private readonly api: ApiService,
    private readonly engApi: EngineeringToolsApiService,
    private readonly router: Router,
  ) { }

  public shippedColor = shippedColor;
  public cleanDescription = cleanDescription;
  public formatDate = formatDate;
  public ProjectCardView = ProjectCardView;

  @ViewChildren(ProjectCardComponent) public projectCards!: QueryList<ProjectCardComponent>;

  public projects: any[] = [];
  public loadingProjects: boolean = false;
  public loadingHotToggle: boolean = false;
  public selectedProject: any = null;
  public selectedView: ProjectCardView = ProjectCardView.None;

  public filterMenuVisible: boolean = false;
  public filterMenuPosition = { x: 0, y: 0 };
  public filterSettings: FilterSettings = new FilterSettings();
  public tempFilterSettings: FilterSettings = new FilterSettings();
  public filterSettingsLastUpdated: Date = new Date(0);

  public loadingOrderDetails: boolean = false;
  public loadingOrderDetailsDots: string = '.';
  public orderDetailsError: boolean = false;
  public orderDetails: any = null;
  public orderDetailsFilterText: string = "";
  public showFriendlyorderDetails: boolean = true;
  public orderDetailsPropertyDisplayConfig = {
    reorderProperties: [
      'Information',
      'Products',
      'StartUp',
      'Freight',
      'DocumentsDrawings',
      'DerivedPrices',
      'DerivedRates',
      'Multipliers',
      'Commission'
    ],
    renameProperties: {
      Information: 'Commercial Information',
      StartUp: 'Start-up',
      DocumentsDrawings: 'Documents and Drawings'
    },
    childConfigs: {
      Information: {
        renameProperties: {
          Order: 'Purchase Order'
        },
        childConfigs: {
          Order: {
            renameProperties: {
              PoNumber: 'PO Number',
              PoValue: 'PO Value',
              PoDate: 'PO Date',
              AcceptedPoDate: 'Accepted PO Date'
            },
            hideProperties: [
              'ProjectNumber',
              'ProjectNumberShort',
              'EndUserProjectId',
              'SalesOrderNumber',
              'ProjectNumberType'
            ]
          }
        }
      }
    }
  };

  public loadingProjectSaves: boolean = false;
  public generatingProjectCalculations: boolean = false;
  public projectSaves: any[] = [];

  hasToolRoute(save: any) {
    return !!toolTypeRouteMap.get(save?.Type);
  }

  formatToolType(type: string) {
    return type?.replace(/([a-z0-9])([A-Z])/g, '$1 $2') ?? '';
  }
  
  ngOnInit() {
    this.updateProjects();
  }

  ngOnDestroy() {
    this.stopOrderDetailsLoadingDots();
  }

  updateProjects() {
    this.projects = [];
    if (!this.filterSettings.activity) {
      this.loadingProjects = true;
      this.api.MasterSchedule.GetMasterScheduleProjects(this.filterSettings)
        .subscribe({
          next: (projects: any[]) => {
            this.projects = projects;
            for (let project of this.projects) {
              project.ItemDescription = this.cleanDescription(project.ItemDescription);
              project.PoDate = this.formatDate(project.PoDate);
              project.RequestedShipDate = this.formatDate(project.RequestedShipDate);
              project.AcknowledgedShipDate = this.formatDate(project.AcknowledgedShipDate);
              project.CurrentShipDate = this.formatDate(project.CurrentShipDate);
              project.ApprovalDrawingDate = this.formatDate(project.ApprovalDrawingDate);
              project.ActualShipDate = this.formatDate(project.ActualShipDate);
            }
            this.loadingProjects = false;
          },
          error: () => {
            this.loadingProjects = false;
          }
        });
    } else {
      this.loadingProjects = false;
    }
    this.filterMenuVisible = false;
    this.filterSettingsLastUpdated = new Date();
  }

  refreshProjectCards(projectIds: number[]) {
    const cards = this.projectCards?.filter(card => projectIds.includes(card.projectData?.Id)) ?? [];
    if (cards.length === 0) {
      return;
    }

    this.loadingProjects = true;
    let pending = cards.length;
    cards.forEach(card => card.reloadProjectData(() => {
      pending--;
      if (pending === 0) {
        this.loadingProjects = false;
      }
    }));
  }

  selectedViewChanged(view: ProjectCardView) {
    this.selectedView = view;
    if (view === ProjectCardView.Schedule) {
      this.filterSettings.startAtToday = false;
    }
    if (view === ProjectCardView.OrderDetails) {
      this.loadingOrderDetails = true;
      this.loadingOrderDetailsDots = '.';
      this.startOrderDetailsLoadingDots();
      this.orderDetailsError = false;
      this.orderDetails = null;
      this.api.Projects.GetProjectOrderDetails(this.selectedProject.Id)
        .subscribe({
          next: (details: any) => {
            if (details === null || details === undefined) {
              this.loadingOrderDetails = false;
              this.stopOrderDetailsLoadingDots();
              this.orderDetailsError = true;
              return;
            }

            while (details && typeof details === 'object') {
              const detailValues = Object.values(details);
              if (detailValues.length !== 1 || detailValues[0] === null || detailValues[0] === undefined) {
                break;
              }
              details = detailValues[0];
            }
            this.orderDetails = details;
            this.orderDetailsFilterText = "";
            this.loadingOrderDetails = false;
            this.stopOrderDetailsLoadingDots();
          },
          error: () => {
            this.loadingOrderDetails = false;
            this.stopOrderDetailsLoadingDots();
            this.orderDetailsError = true;
          }
        });
    }
    if (view === ProjectCardView.EngineeringCalculations) {
      this.loadProjectSaves();
    }
  }

  private orderDetailsLoadingDotsTimer: ReturnType<typeof setInterval> | null = null;

  private startOrderDetailsLoadingDots() {
    this.stopOrderDetailsLoadingDots();
    this.orderDetailsLoadingDotsTimer = setInterval(() => {
      this.loadingOrderDetailsDots = this.loadingOrderDetailsDots.length === 3
        ? '.'
        : `${this.loadingOrderDetailsDots}.`;
    }, 350);
  }

  private stopOrderDetailsLoadingDots() {
    if (this.orderDetailsLoadingDotsTimer) {
      clearInterval(this.orderDetailsLoadingDotsTimer);
      this.orderDetailsLoadingDotsTimer = null;
    }
  }

  loadProjectSaves() {
    this.loadingProjectSaves = true;
    this.engApi.GetAllProjectComponentSaves(this.selectedProject.ProjectNumber)
      .subscribe((saves: any[]) => {
        this.projectSaves = saves;
        this.loadingProjectSaves = false;
      });
  }

  generateCalculations() {
    const projectNumber = this.selectedProject?.ProjectNumber;
    if (!projectNumber || this.generatingProjectCalculations) {
      return;
    }

    let reloadScheduled = false;
    const scheduleReload = () => {
      if (reloadScheduled) {
        return;
      }
      reloadScheduled = true;

      setTimeout(() => {
        this.engApi.GetAllProjectComponentSaves(projectNumber)
          .subscribe({
            next: (saves: any[]) => {
              this.projectSaves = saves;
            },
            error: () => {
              this.loadingProjectSaves = false;
              this.generatingProjectCalculations = false;
            },
            complete: () => {
              this.loadingProjectSaves = false;
              this.generatingProjectCalculations = false;
            }
          });
      }, 10000);
    };

    this.generatingProjectCalculations = true;
    this.loadingProjectSaves = true;
    this.projectSaves = [];
    this.engApi.CreateDefaultProjectCalculations(projectNumber)
      .subscribe({
        next: () => scheduleReload(),
        complete: () => scheduleReload(),
        error: () => {
          this.loadingProjectSaves = false;
          this.generatingProjectCalculations = false;
        }
      });
  }

  openToolSave(save: any) {
    const route = toolTypeRouteMap.get(save?.Type);
    const saveId = save?.Id;

    if (!route || !saveId) {
      return;
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/', route, saveId])
    );
    window.open(`${window.location.origin}/#${url}`, '_blank');
  }

}
